import { useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { humanDate } from '../lib/format';
import { Modal } from '../ui/Modal';
import { useOnline } from './net';
import { dismissErrors, keepMine, takeServer, useOutbox } from './outbox';
import { afterConflict, useSync } from './sync';

/* Состояние связи в шапке кабинета (как в Google Таблицах): нет связи → синхронизация →
   «всё сохранено» и пропадает. Тренер всегда видит, где сейчас его правки. */

const hhmm = (t: number) => new Date(t).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
const plural = (n: number) => (n % 10 === 1 && n % 100 !== 11 ? 'изменение' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? 'изменения' : 'изменений');

/** Состояние связи в шапке кабинета (Shell → Topbar): без сети, синхронизация, «всё сохранено» */
export function NetPill() {
  const online = useOnline();
  const ob = useOutbox();
  const sync = useSync();
  let cls = '';
  let text = '';
  let tip = '';
  if (!online) {
    cls = 'off';
    text = 'Нет связи с сервером' + (ob.pending ? ` · не отправлено: ${ob.pending}` : '');
    tip = 'Правки сохраняются на этом устройстве и уйдут на сервер, когда появится интернет' + (sync.syncedAt ? `. Данные с сервера на ${hhmm(sync.syncedAt)}` : '');
  } else if (sync.phase === 'syncing' || ob.sending || ob.pending) {
    cls = 'sync';
    text = 'Связь восстановлена · синхронизация…' + (ob.pending ? ` ${ob.pending}` : '');
    tip = ob.pending ? `Отправляется ${ob.pending} ${plural(ob.pending)}, затем загрузятся свежие данные` : 'Загружаются свежие данные';
  } else if (sync.phase === 'done') {
    cls = 'ok';
    text = 'Всё сохранено';
    tip = 'Все изменения на сервере, данные обновлены';
  }
  if (!cls) return null;
  return (
    <span className={'netpill ' + cls} role="status" aria-live="polite" data-tip={tip}>
      <i />
      <span>{text}</span>
    </span>
  );
}

/** Поверх страницы: правки, которые сервер не принял, и окно конфликта */
export function OfflineBar() {
  const ob = useOutbox();
  return createPortal(
    <>
      {ob.errors.length ? (
        <div className="netbar err" role="alert">
          <i />
          <span>
            Не сохранилось: {ob.errors[ob.errors.length - 1]}
            {ob.errors.length > 1 ? ` (и ещё ${ob.errors.length - 1})` : ''}
          </span>
          <button onClick={dismissErrors} aria-label="Закрыть">
            ✕
          </button>
        </div>
      ) : null}
      {ob.conflict ? <Conflict /> : null}
    </>,
    document.body,
  );
}

/** День изменили на другом устройстве, пока правки лежали в очереди */
function Conflict() {
  const ob = useOutbox();
  const qc = useQueryClient();
  const c = ob.conflict!;
  const who = c.o.input.who;
  const clients = (qc.getQueryData([['trainer', 'clients'], { type: 'query' }]) as { id: string; first?: string | null; last?: string | null; name?: string }[] | undefined) || [];
  const groups = (qc.getQueryData([['trainer', 'groups'], { type: 'query' }]) as { id: string; name: string }[] | undefined) || [];
  const name =
    'clientId' in who
      ? (() => {
          const x = clients.find((y) => y.id === who.clientId);
          return x ? x.name || [x.first, x.last].filter(Boolean).join(' ') : 'клиента';
        })()
      : groups.find((g) => g.id === who.groupId)?.name || 'группы';
  const date = 'date' in c.o.input ? humanDate(c.o.input.date) : '';
  const close = () => {};
  return (
    <Modal
      title="Тренировку изменили на другом устройстве"
      onClose={close}
      className="netconf"
      foot={
        <>
          <button
            className="btn gh"
            onClick={() => {
              takeServer();
              afterConflict();
            }}
          >
            Взять с сервера
          </button>
          <button
            className="btn"
            onClick={() => {
              keepMine();
              afterConflict();
            }}
          >
            Оставить мою версию
          </button>
        </>
      }
    >
      <p>
        Пока не было связи, тренировку {name}
        {date ? ` на ${date}` : ''} изменили в кабинете на другом устройстве.
      </p>
      <p>«Оставить мою версию» — на сервер уйдут ваши правки с этого устройства. «Взять с сервера» — ваши неотправленные правки этого дня отменятся.</p>
    </Modal>
  );
}
